/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { State } from "store/reducers";
import ls from "Localization";
import _ from "lodash";
import { Helmet } from "react-helmet-async";

import actions from "store/actions";

import { DasState } from "store/reducers/das";
import IDas, { DasDeliveryStatus, DasStatus } from "Types/Das";
import dayjs from "dayjs";
import Report from "Components/Report";
import { dateSort } from "Components/Report/helpers";
import { toStringCurrency } from "helpers/string";

import ImportModal from "./Import";
import SendModal from "./Send";
import ReactTable from "react-table";
import { Checkbox, CircularProgress, Fab, Link, Tooltip } from "@mui/material";
import RedoOutlinedIcon from "@mui/icons-material/RedoOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import LaunchIcon from '@mui/icons-material/Launch';
import SendDasModal from "./Modals/SendDasModal";
import DateSelectFilter from "Components/Report/DateSelectorFilter";

const ls2: any = ls;

let getDasesDebounced: (() => any) & _.Cancelable = _.debounce(() => {}, 250);

function Dases() {
	const [checked, setChecked] = useState<string[]>([]);
	const [refreshing, setRefreshing] = useState<string[]>([]);
	const [messageModal, setMessageModal] = useState<React.ReactNode | null>(
		null
	);

	const hideResendMessageModal = () => {
		setMessageModal(null);
	};

	const dispatch = useDispatch<any>();
	const table = useRef<ReactTable<any>>(null);

	const state = useSelector<State, DasState>(s => s.das);

	const handleSend = (id, sendEmail, sendWhatsApp) => {
		dispatch(
			actions.das.send(id, sendEmail, sendWhatsApp, (error, result) => {
				if (error) {
					console.log(error);
				} else {
					load();
					hideResendMessageModal();
				}
			})
		);
	};

	const handleResendMessage = (id?: string) => {
		setMessageModal(
			<SendDasModal
				checked={checked}
				id={id}
				handleSend={handleSend}
				hideResendMessageModal={hideResendMessageModal}
			/>
		);
	};

	const handleRefreshDas = (id: string) => {
		setRefreshing(prev => [...prev, id]);

		dispatch(
			actions.das.refreshDas(id, error => {
				if (error) {
					setRefreshing(prev => prev.filter(i => i !== id));
					return;
				}

				// Worker re-downloads async; reload the table a few times to surface it.
				let count = 0;
				const interval = setInterval(() => {
					count += 1;
					load();
					if (count >= 5) {
						clearInterval(interval);
						setRefreshing(prev => prev.filter(i => i !== id));
					}
				}, 4000);
			})
		);
	};

	const handleFetchData = (tableState: any) => {
		if (!tableState) return;
		const { page, pageSize, toExport, callback } = tableState;
		let { sorted, filtered } = tableState;

		if (getDasesDebounced) {
			getDasesDebounced.cancel();
		}

		if (!sorted) sorted = [];

		const creationDate = filtered.find((c: any) => c.id === "dueDate");

		if (creationDate?.value) {
			filtered = filtered.filter((c: any) => c.id !== "dueDate");

			if (creationDate?.value.startDate) {
				filtered.push({
					id: "startDate",
					value: creationDate.value.startDate
				});
			}

			if (creationDate?.value.endDate)
				filtered.push({
					id: "endDate",
					value: creationDate.value.endDate
				});
		}

		getDasesDebounced = _.debounce(
			() =>
				dispatch(
					actions.das.getDases(
						page * pageSize,
						pageSize,
						filtered.reduce(
							(p: any, c: any) => p + `&filters[${c.id}]=${c.value}`,
							""
						),
						sorted[0]?.id,
						sorted[0]?.desc,
						toExport,
						callback
					)
				),
			500
		);

		getDasesDebounced();
	};

	const load = () => handleFetchData(table.current?.state);

	const columns = [
		{
			Header: p => (
				<Checkbox
					id={"all"}
					checked={checked.filter(i => i === "all").length > 0 ? true : false}
					color="primary"
					style={{ padding: 4 }}
					inputProps={{ "aria-label": "primary checkbox" }}
					onChange={e => {
						if (
							checked.length === state.items.length ||
							checked.includes("all")
						) {
							setChecked([]);
						} else {
							const allChecked: string[] = [];
							state.items.forEach(p => {
								if (p._id) {
									allChecked.push(p._id);
								}
							});
							setChecked(allChecked.concat("all"));
						}
					}}
				/>
			),
			id: "msg",
			dontExport: true,
			show: true,
			accessor: p => (
				<Checkbox
					checked={checked.filter(i => i === p._id).length > 0 ? true : false}
					id={p._id}
					color="primary"
					style={{ padding: 4 }}
					inputProps={{ "aria-label": "secondary checkbox" }}
					onChange={e => {
						if (checked.length <= 0) {
							setChecked([...checked, e.target.id]);
						} else {
							//checa se o check está selecionado
							const filter = checked.filter(c => c === e.target.id);
							if (filter.length) {
								//retira o check
								const unCheck = checked.filter(c => c !== e.target.id);
								setChecked(unCheck);
							} else {
								setChecked([...checked, e.target.id]);
							}
						}
					}}
				/>
			),
			width: 45,
			sortable: false,
			filterable: false
		},
		{
			Header: ls.client,
			id: "mei",
			show: true,
			accessor: (c: IDas) => {
				if (c.mei) {
					return c.mei.businessName || c.mei.personName || c.mei.cnpj;
				}
				return "";
			}
		},
		{
			Header: ls.cnpj,
			id: "cnpj",
			show: true,
			accessor: (c: IDas) => {
				if (c.mei) {
					return c.mei.cnpj;
				}
				return "";
			},
			width: 130
		},
		{
			Header: "Período",
			id: "period",
			accessor: "period",
			show: true,
			width: 130
		},
		{
			Header: ls.value,
			id: "value",
			show: true,
			filterable: false,
			accessor: (c: IDas) =>
				c.total ? toStringCurrency(+c.total / 100.0) : "",
			width: 100
		},
		{
			Header: ls.dueDate,
			id: "dueDate",
			accessor: (c: IDas) =>
				c.dueDate ? dayjs(c.dueDate).format(ls.dateFormatWithoutSeconds) : "",
			show: true,
			sortMethod: dateSort,
			width: 150,
			Filter: p => <DateSelectFilter {...p} />
		},
		{
			Header: "Cód. barras",
			id: "barCode",
			show: true,
			accessor: "barCode"
		},
		{
			Header: "Boleto",
			id: "boleto",
			show: true,
			filterable: false,
			accessor: (c: IDas) =>
				c.link ? (
					<Link
						href={c.link}
						target="_blank"
						rel="noopener noreferrer"
						style={{
							display: "flex",
							alignItems: "center",
							textDecoration: "none"
						}}
					>
						<LaunchIcon style={{ marginRight: 5 }} />
						Abrir boleto
					</Link>
				) : null,
			width: 140
		},
		{
			Header: ls.status,
			id: "status",
			show: true,
			accessor: (c: IDas) => {
				if (c.status !== undefined) {
					if (c.status === DasStatus.due) {
						return (
							<Tooltip title={c.status}>{ls2[DasStatus[c.status]]}</Tooltip>
						);
					}
					return ls2[DasStatus[c.status]];
				}
			},
			width: 140,
			Filter: ({ filter, onChange }: any) => {
				return (
					<select
						onChange={event => onChange(event.target.value)}
						style={{ width: "100%" }}
						value={filter ? filter.value : "all"}
					>
						<option value="">Todos</option>
						{Object.keys(DasStatus)
							.filter(c => !isNaN(Number(c)))
							.map((c: any) => (
								<option key={c} value={c}>
									{ls2[DasStatus[c]]}
								</option>
							))}
					</select>
				);
			}
		},
		{
			Header: "E-mail",
			id: "email-status",
			show: true,
			width: 140,
			filterable: false,
			accessor: (c: IDas) =>
				c.deliveries && c.deliveries.length > 0
					? ls2[DasDeliveryStatus[c.deliveries[0].status]]
					: ""
		},
		{
			Header: ls.actions,
			id: "actions",
			accessor: (c: any) => (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flex: 1
					}}
				>
					{refreshing.includes(c._id) ? (
						<CircularProgress size={18} style={{ marginRight: 12 }} />
					) : (
						<a
							href="!#"
							style={{ marginRight: 12 }}
							onClick={e => {
								e.preventDefault();
								handleRefreshDas(c._id);
							}}
						>
							<Tooltip title="Atualizar DAS">
								<SyncIcon />
							</Tooltip>
						</a>
					)}
					<a
						href="!#"
						onClick={e => {
							e.preventDefault();
							handleResendMessage(c._id);
						}}
					>
						<Tooltip title="Enviar:">
							<RedoOutlinedIcon />
						</Tooltip>
					</a>
				</div>
			),
			show: true,
			filterable: false,
			width: 80
		}
	];

	return (
		<div>
			<Helmet>
				<title>Impostos - DAS | {ls.appName}</title>
			</Helmet>
			<Report
				manual
				tableRef={table}
				headerRightComponent={
					<>
						<SendModal load={load} />
						<ImportModal load={load} />
					</>
				}
				titleText="Impostos - DAS"
				title="Impostos - DAS"
				data={state.items}
				pages={state.pages}
				loading={state.loading.getAll}
				columns={columns}
				onFetchData={handleFetchData}
				filterable
				visibleColumns={columns
					.filter(c => c.show)
					.map(c => c.Header.toString())}
				defaultSorted={[
					{
						id: "createdAt",
						desc: true
					}
				]}
				getTdProps={() => {
					return {
						style: {
							display: "flex",
							alignItems: "center"
						}
					};
				}}
			/>

			{messageModal}
			{checked.length >= 1 && (
				<Tooltip title="Enviar para:">
					<Fab
						color="primary"
						aria-label="Send"
						style={{
							position: "fixed",
							bottom: "16px",
							right: "16px",
							zIndex: 1000
						}}
						onClick={() => handleResendMessage()}
					>
						<RedoOutlinedIcon />
					</Fab>
				</Tooltip>
			)}
		</div>
	);
}

export default Dases;
